import React from 'react';
import { useTranslation } from 'react-i18next';
import { isNativeApp, requestApp } from 'common/webview';
import { isInKorea, isKoreaByBrowser } from 'common/utils/regionDetector';
import { useBackDismiss } from 'hooks/useBackDismiss';
import { IcPin, IcClipboard } from 'components/common/icons/EmojiIcons';
import kakaoIcon from 'assets/icons/map-kakao.svg';
import naverIcon from 'assets/icons/map-naver.svg';
import tmapIcon from 'assets/icons/map-tmap.svg';
import googleIcon from 'assets/icons/map-google.svg';
import styles from './ExternalMapSheet.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

interface ExternalMapSheetProps {
    visible: boolean;
    placeName: string;
    address: string;
    lat: number;
    lng: number;
    onClose: () => void;
}

/**
 * 외부 지도앱 연결 바텀시트
 * - 국내 장소: 카카오맵, 네이버지도, 티맵, 구글맵
 * - 해외 장소: 구글맵만
 *
 * 기준은 사용자 언어가 아니라 그 장소의 좌표입니다. 한국어 사용자라도 해외 장소를
 * 볼 때 카카오·네이버·티맵을 열면 국내 지도라 아무것도 안 보입니다.
 */
export const ExternalMapSheet: React.FC<ExternalMapSheetProps> = ({
    visible, placeName, address, lat, lng, onClose,
}) => {
    const { i18n } = useTranslation();
    // 좌표가 있으면 좌표로, 없으면 사용자 환경으로 판단합니다
    const domestic = (lat && lng) ? isInKorea(lat, lng) : (i18n.language === 'ko' || isKoreaByBrowser());
    const encodedName = encodeURIComponent(placeName);

    const mapApps = [
        ...(domestic ? [
            {
                id: 'kakao',
                name: '카카오맵',
                icon: kakaoIcon,
                url: `kakaomap://look?p=${lat},${lng}`,
                webUrl: `https://map.kakao.com/link/map/${encodedName},${lat},${lng}`,
            },
            {
                id: 'naver',
                name: '네이버지도',
                icon: naverIcon,
                url: `nmap://place?lat=${lat}&lng=${lng}&name=${encodedName}&appname=com.dokoiot.dokocrew`,
                webUrl: `https://map.naver.com/v5/search/${encodedName}?c=${lng},${lat},15,0,0,0,dh`,
            },
            {
                id: 'tmap',
                name: '티맵',
                icon: tmapIcon,
                url: `tmap://route?goalx=${lng}&goaly=${lat}&goalname=${encodedName}`,
                webUrl: `https://www.tmap.co.kr/tmap2/mobile/route.jsp?goalx=${lng}&goaly=${lat}&goalname=${encodedName}`,
            },
        ] : []),
        {
            id: 'google',
            name: 'Google Maps',
            icon: googleIcon,
            url: `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
            webUrl: `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
        },
    ];

    const handleOpen = (app: typeof mapApps[0]) => {
        if (isNativeApp()) {
            // 네이티브: app scheme URL로 직접 열기 시도
            requestApp('OPEN_URL', { url: app.url, fallbackUrl: app.webUrl });
        } else {
            // 웹: https URL로 열기
            window.open(app.webUrl, '_blank');
        }
        onClose();
    };

    const handleCopyAddress = async () => {
        try {
            await navigator.clipboard.writeText(address || `${lat}, ${lng}`);
        } catch {
            const ta = document.createElement('textarea');
            ta.value = address || `${lat}, ${lng}`;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }
        onClose();
    };

    useBackDismiss(visible, onClose);
    if (!visible) return null;

    return (
        <div className={cls("ems-overlay")} onClick={onClose}>
            <div className={cls("ems-sheet")} onClick={e => e.stopPropagation()}>
                <div className={cls("ems-handle")}><div className={cls("ems-handle-bar")} /></div>

                <div className={cls("ems-place-info")}>
                    <div className={cls("ems-place-name")}><IcPin size={16} /> {placeName}</div>
                    {address && <div className={cls("ems-place-addr")}>{address}</div>}
                </div>

                <div className={cls("ems-app-grid")}>
                    {mapApps.map(app => (
                        <button key={app.id} className={cls("ems-app-btn")} onClick={() => handleOpen(app)}>
                            <img src={app.icon} alt={app.name} className={cls("ems-app-icon-img")} />
                            <span className={cls("ems-app-name")}>{app.name}</span>
                        </button>
                    ))}
                </div>

                <div className={cls("ems-actions")}>
                    <button className={cls("ems-action-btn")} onClick={handleCopyAddress}>
                        <IcClipboard size={16} /> 주소 복사
                    </button>
                    <button className={cls("ems-action-btn cancel")} onClick={onClose}>
                        닫기
                    </button>
                </div>
            </div>
        </div>
    );
};
